import { useEffect, useState, useCallback, useRef, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Canvas, useThree, type ThreeEvent } from "@react-three/fiber";
import { OrbitControls, Grid, useGLTF, Html } from "@react-three/drei";
import * as THREE from "three";
import { useStore } from "../store";
import { placeRobot, generateEnvironmentVariations, getEnvironmentById } from "../api";
import type { Environment, SemanticCategory, RobotPlacement } from "../types";

/* ------------------------------------------------------------------ */
/*  Semantic overlay color map                                         */
/* ------------------------------------------------------------------ */

const SEMANTIC_COLORS: Record<SemanticCategory, string> = {
  floor: "#34d399",
  wall: "#60a5fa",
  obstacle: "#f87171",
  surface: "#fbbf24",
  target: "#c084fc",
  unknown: "#9298b0",
};

/* ------------------------------------------------------------------ */
/*  Sub-components rendered inside Canvas                              */
/* ------------------------------------------------------------------ */

function EnvironmentModel({
  url,
  semanticTags,
}: {
  url: string;
  semanticTags: NonNullable<Environment["semantic_tags"]>;
}) {
  const { scene } = useGLTF(url);
  const clonedScene = scene.clone(true);

  // Apply semantic overlay colors to tagged meshes
  useEffect(() => {
    if (semanticTags.length === 0) return;

    const tagMap = new Map(semanticTags.map((tag) => [tag.mesh_id, tag]));

    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && tagMap.has(child.name)) {
        const tag = tagMap.get(child.name)!;
        const color = SEMANTIC_COLORS[tag.category] ?? SEMANTIC_COLORS.unknown;
        const material = new THREE.MeshStandardMaterial({
          color,
          transparent: true,
          opacity: 0.35,
          side: THREE.DoubleSide,
        });
        child.material = material;
      }
    });
  }, [clonedScene, semanticTags]);

  return <primitive object={clonedScene} />;
}

function RobotMarker({ position }: { position: [number, number, number] }) {
  return (
    <mesh position={position}>
      <cylinderGeometry args={[0.15, 0.15, 0.4, 16]} />
      <meshStandardMaterial color="#4f8cff" />
      {/* Small cone on top to indicate direction */}
      <mesh position={[0, 0.35, 0]}>
        <coneGeometry args={[0.12, 0.25, 16]} />
        <meshStandardMaterial color="#6ba0ff" />
      </mesh>
    </mesh>
  );
}

function ClickPlane({
  onPlaceRobot,
}: {
  onPlaceRobot: (point: THREE.Vector3) => void;
}) {
  const handleClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      e.stopPropagation();
      onPlaceRobot(e.point);
    },
    [onPlaceRobot],
  );

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.01, 0]}
      visible={false}
      onClick={handleClick}
    >
      <planeGeometry args={[100, 100]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
}

function SceneSetup() {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(5, 5, 5);
    camera.lookAt(0, 0, 0);
  }, [camera]);

  return null;
}

function LoadingFallback() {
  return (
    <Html center>
      <div style={{ color: "var(--color-text-muted)", fontSize: 14 }}>
        Loading 3D environment...
      </div>
    </Html>
  );
}

/* ------------------------------------------------------------------ */
/*  Toolbar mode type                                                  */
/* ------------------------------------------------------------------ */

type ToolMode = "orbit" | "place_robot" | "set_task";

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function EnvironmentViewer() {
  const { id: projectId, envId } = useParams<{
    id: string;
    envId: string;
  }>();
  const navigate = useNavigate();
  const fetchEnvironment = useStore((s) => s.fetchEnvironment);
  const environments = useStore((s) => s.environments);
  const loading = useStore((s) => s.loading);
  const error = useStore((s) => s.error);
  const clearError = useStore((s) => s.clearError);

  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [toolMode, setToolMode] = useState<ToolMode>("orbit");
  const [robotPlacement, setRobotPlacement] = useState<RobotPlacement | null>(
    null,
  );
  const [placingRobot, setPlacingRobot] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Variation generation state
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [variationCount, setVariationCount] = useState(5);
  const [variationError, setVariationError] = useState<string | null>(null);
  const [variationSuccess, setVariationSuccess] = useState<string | null>(null);
  const [pendingVariants, setPendingVariants] = useState<Environment[]>([]);
  const [readyVariants, setReadyVariants] = useState(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);

  // Fetch environment data
  useEffect(() => {
    if (!projectId || !envId) return;

    const existing = environments.find((e) => e.id === envId);
    if (existing) {
      setEnvironment(existing);
    }

    fetchEnvironment(projectId, envId)
      .then((env) => setEnvironment(env))
      .catch((e) => setLoadError((e as Error).message));
  }, [projectId, envId, fetchEnvironment, environments]);

  const handlePlaceRobot = useCallback(
    async (point: THREE.Vector3) => {
      if (toolMode !== "place_robot" || !projectId || !envId) return;

      const placement: RobotPlacement = {
        position: [point.x, point.y, point.z],
        rotation: [0, 0, 0],
        robot_model: "UR5",
      };

      setRobotPlacement(placement);
      setPlacingRobot(true);

      try {
        await placeRobot(projectId, envId, placement);
      } catch (e) {
        // No placement endpoint exists server-side yet — keep the local
        // marker but log the truth instead of faking a persisted save.
        // eslint-disable-next-line no-console
        console.warn(
          "[console] robot placement not persisted (endpoint missing):",
          (e as Error).message,
        );
      } finally {
        setPlacingRobot(false);
        setToolMode("orbit");
      }
    },
    [toolMode, projectId, envId],
  );

  const handleStartTraining = useCallback(() => {
    if (projectId) {
      navigate(`/projects/${projectId}/training`);
    }
  }, [projectId, navigate]);

  async function handleGenerateVariations() {
    if (!environment) return;
    setGeneratingVariations(true);
    setVariationError(null);
    setVariationSuccess(null);
    setPendingVariants([]);
    setReadyVariants(0);
    try {
      const variants = await generateEnvironmentVariations(environment.id, {
        count: variationCount,
        randomize: ["lighting", "object_positions", "textures"],
      });
      setPendingVariants(variants);
      setVariationSuccess(`Requested ${variants.length} variation${variants.length !== 1 ? "s" : ""}`);
    } catch (e: any) {
      setVariationError(e?.message ?? "Failed to request variations");
    } finally {
      setGeneratingVariations(false);
    }
  }

  // Poll pending variants every 5 s until all reach a terminal status
  useEffect(() => {
    if (pendingVariants.length === 0) return;

    pollTimerRef.current = setInterval(async () => {
      const updated: Environment[] = [];
      let readyCount = 0;
      let allTerminal = true;

      for (const v of pendingVariants) {
        try {
          const fresh = await getEnvironmentById(v.id);
          updated.push(fresh);
          if (fresh.status === "ready") readyCount++;
          if (fresh.status !== "ready" && fresh.status !== "failed") {
            allTerminal = false;
          }
        } catch {
          updated.push(v);
          allTerminal = false;
        }
      }

      setPendingVariants(updated);
      setReadyVariants(readyCount);

      if (allTerminal && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }, 5000);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [pendingVariants.length]); // only re-run when variant count changes

  // Error state
  if (loadError) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Environment Viewer</h1>
        </div>
        <div
          className="card"
          style={{ maxWidth: 600, borderColor: "var(--color-danger)" }}
        >
          <h3
            className="card-title"
            style={{ color: "var(--color-danger)", marginBottom: 12 }}
          >
            Failed to Load Environment
          </h3>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            {loadError}
          </p>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setLoadError(null);
              if (projectId && envId) {
                fetchEnvironment(projectId, envId)
                  .then((env) => setEnvironment(env))
                  .catch((e) => setLoadError((e as Error).message));
              }
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && !environment) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Environment Viewer</h1>
        </div>
        <p className="text-muted">Loading environment...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {environment?.name ?? "Environment Viewer"}
          </h1>
          <p className="page-subtitle">
            Inspect and configure the 3D environment
          </p>
        </div>
        {environment && (
          <span
            className={`badge ${
              environment.status === "ready"
                ? "badge-success"
                : environment.status === "processing"
                  ? "badge-warning"
                  : environment.status === "failed"
                    ? "badge-danger"
                    : "badge-info"
            }`}
          >
            {environment.status}
          </span>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="card mb-4"
          style={{ borderColor: "var(--color-danger)" }}
        >
          <div className="flex justify-between items-center">
            <span style={{ color: "var(--color-danger)" }}>{error}</span>
            <button className="btn btn-sm btn-secondary" onClick={clearError}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar mb-4">
        <button
          className={`btn btn-sm ${toolMode === "orbit" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setToolMode("orbit")}
        >
          Orbit
        </button>
        <button
          className={`btn btn-sm ${toolMode === "place_robot" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setToolMode("place_robot")}
          disabled={placingRobot}
        >
          {placingRobot ? "Placing..." : "Add Robot"}
        </button>
        <button
          className={`btn btn-sm ${toolMode === "set_task" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setToolMode("set_task")}
        >
          Set Task
        </button>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-sm btn-primary"
          onClick={handleStartTraining}
        >
          Start Training
        </button>
      </div>

      {/* Semantic tag legend */}
      {environment && (environment.semantic_tags?.length ?? 0) > 0 && (
        <div className="tag-legend mb-4">
          {(environment.semantic_tags ?? []).map((tag) => (
            <div key={tag.id} className="tag-legend-item">
              <div
                className="tag-legend-swatch"
                style={{
                  backgroundColor:
                    SEMANTIC_COLORS[tag.category] ?? SEMANTIC_COLORS.unknown,
                }}
              />
              {tag.label}
            </div>
          ))}
        </div>
      )}

      {/* 3D Canvas */}
      <div className="viewer-container" ref={canvasRef}>
        <Canvas shadows>
          <SceneSetup />

          {/* Lighting */}
          <ambientLight intensity={0.4} />
          <directionalLight
            position={[10, 15, 10]}
            intensity={1}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
          />
          <directionalLight position={[-5, 8, -5]} intensity={0.3} />

          {/* Grid */}
          <Grid
            args={[50, 50]}
            cellSize={1}
            cellThickness={0.5}
            cellColor="#2e3347"
            sectionSize={5}
            sectionThickness={1}
            sectionColor="#3a3f55"
            fadeDistance={30}
            position={[0, 0, 0]}
          />

          {/* Environment model */}
          {environment?.asset_url && (
            <Suspense fallback={<LoadingFallback />}>
              <EnvironmentModel
                url={environment.asset_url}
                semanticTags={environment.semantic_tags ?? []}
              />
            </Suspense>
          )}

          {/* Click plane for robot placement */}
          {toolMode === "place_robot" && (
            <ClickPlane onPlaceRobot={handlePlaceRobot} />
          )}

          {/* Robot marker */}
          {robotPlacement && (
            <RobotMarker position={robotPlacement.position} />
          )}

          {/* Camera controls */}
          <OrbitControls
            enablePan
            enableZoom
            enableRotate={toolMode === "orbit"}
            makeDefault
          />
        </Canvas>

        {/* Tool mode indicator overlay */}
        {toolMode !== "orbit" && (
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              padding: "6px 12px",
              background: "rgba(0, 0, 0, 0.7)",
              borderRadius: "var(--radius-md)",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--color-accent)",
            }}
          >
            {toolMode === "place_robot"
              ? "Click to place robot"
              : "Click to set task target"}
          </div>
        )}
      </div>

      {/* Domain randomization */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="card-title">Domain randomization</h3>
        <p className="card-meta">
          Generate multiple visual variants of this scan (lighting, object
          positions, textures). Training across variants improves sim-to-real
          transfer.
        </p>
        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="form-label" htmlFor="variation-count">
            Number of variations
          </label>
          <input
            id="variation-count"
            className="form-input"
            type="number"
            min={1}
            max={20}
            value={variationCount}
            onChange={(e) =>
              setVariationCount(
                Math.min(20, Math.max(1, parseInt(e.target.value, 10) || 1)),
              )
            }
            style={{ width: 100 }}
            disabled={generatingVariations}
          />
        </div>
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="btn btn-primary"
            onClick={handleGenerateVariations}
            disabled={generatingVariations || !environment}
          >
            {generatingVariations ? "Requesting…" : "Generate variations"}
          </button>
          {variationError && (
            <span className="badge badge-danger">{variationError}</span>
          )}
          {variationSuccess !== null && !variationError && pendingVariants.length === 0 && (
            <span className="badge badge-success">{variationSuccess}</span>
          )}
        </div>
        {pendingVariants.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="form-label">
              Variants{" "}
              <span
                className={
                  readyVariants === pendingVariants.length
                    ? "badge badge-success"
                    : "badge badge-info"
                }
              >
                {readyVariants} / {pendingVariants.length} ready
              </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-bar-fill"
                style={{
                  width: `${(readyVariants / Math.max(pendingVariants.length, 1)) * 100}%`,
                }}
              />
            </div>
            <div className="text-sm text-muted" style={{ marginTop: 4 }}>
              {pendingVariants.map((v) => v.name || v.id.slice(0, 8)).join(", ")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
